/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-22 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import {
  ButtonGroup,
  Page,
  Icon,
  Layout,
  Card,
  Button,
  Badge,
  TextStyle,
  DisplayText,
  Heading,
  Grid,
  RadioButton
} from '@shopify/polaris';
import { ExternalSmallMinor } from '@shopify/polaris-icons';

import { AI_POWERED_RECOMMENDATION_NAME } from 'src/constants/subscription';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';

import { checkPersonalizationCampaignRemainingDays, confirmLeavePage } from 'src/helpers';
import { hasPermissionRecommendation } from 'src/helpers/permission-helper';
import history from 'src/routes/history';

import { FullscreenBar } from 'src/components/views/common/fullscreenBar';
import { IconLikeContent, IconWandContent } from 'src/constants/icon';
import { BadgeAI } from 'src/components/containers/common/icon';
import { routes } from 'src/routes';

import {
  RECOMMENDATION_WIDGET_PAGE_TYPES,
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES_WITH_PAGE_TYPES,
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES_WITH_PAGE_TYPES_FOR_FREEMIUM,
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES,
  PERSONALIZED_RECOMMENDATION
} from 'src/constants/recommendation';

import type {
  ComposedProps,
  State,
  RenderSelectPageTypePropsType,
  RenderSelectRecommendationTypePropsType
} from './index.type';
import CrownPremium from 'src/assets/images/common/crown-red.png';

const RenderSelectPageType = (props: RenderSelectPageTypePropsType) => {
  const { i18n, handleSelectPageType, pageType } = props;
  return (
    <div className="general-items page-items">
      <Heading>{i18n.translate('AdminHTML.Recommendation.NewWidgetItem.pageTypeTitle')}</Heading>
      <Grid>
        {_.map(RECOMMENDATION_WIDGET_PAGE_TYPES, (item, key) => {
          let { name, icon } = item;
          name = i18n.translate(name);
          const isSelected = pageType === key;

          return (
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }} key={key}>
              <div className={`button-large ${isSelected ? 'isSelected' : ''}`}>
                <Button fullWidth onClick={() => handleSelectPageType(key, name)}>
                  <div className="btn-title">
                    <div className="btn-title-icon">
                      <Icon source={icon} color="base" />
                    </div>
                    <div className="btn-title-content">{name}</div>
                  </div>
                </Button>
              </div>
            </Grid.Cell>
          );
        })}
      </Grid>
    </div>
  );
};

const RenderSelectRecommendationType = (props: RenderSelectRecommendationTypePropsType) => {
  const {
    i18n,
    pageType,
    selectedPlan,
    recommendationType,
    handleSelectRecommendationType,
    handleUnlock,
    tenantApp,
    disableLockUnLock
  } = props;

  let recommendationTypes =
    pageType !== ''
      ? RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES_WITH_PAGE_TYPES[pageType]
      : RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES_WITH_PAGE_TYPES['product-page'];

  if (selectedPlan.includes('freemium')) {
    recommendationTypes =
      RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES_WITH_PAGE_TYPES_FOR_FREEMIUM[
        pageType !== '' ? pageType : 'product-page'
      ];
  }

  return (
    <div className="general-items type-items">
      <Heading>
        {i18n.translate('AdminHTML.Recommendation.NewWidgetItem.recommendationTypeTitle')}
      </Heading>
      <Grid>
        {_.map(recommendationTypes, (item, key) => {
          const { mostUsed, premium } = item;
          const isFreePlan = selectedPlan.includes('freemium');
          const enabledRecommendationType = _.get(
            tenantApp,
            `settings.recommendation.settings.${key}`,
            key === PERSONALIZED_RECOMMENDATION ? false : true
          );

          const remainingDays =
            key === PERSONALIZED_RECOMMENDATION ? checkPersonalizationCampaignRemainingDays() : -1;

          let {
            name,
            defaultName,
            description,
            isAIPowered = false
          } = RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES[key];
          name = i18n.translate(name);
          defaultName = defaultName ? i18n.translate(defaultName) : name;
          description = i18n.translate(description);
          const isSelected = recommendationType === key;

          // Addon recommendation locked
          if (premium) {
            return (
              <Grid.Cell columnSpan={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} key={key}>
                <div
                  className={`button-large ${isSelected ? 'isSelected' : ''} ${
                    !enabledRecommendationType ? 'disabled' : ''
                  }`}>
                  <Button
                    fullWidth
                    onClick={() =>
                      !enabledRecommendationType
                        ? handleUnlock()
                        : handleSelectRecommendationType(key, defaultName)
                    }>
                    {remainingDays !== -1 && (
                      <div className="BC-MarketingCampaign_remainingDays">
                        <Badge status={remainingDays > 5 ? 'attention' : 'critical'}>
                          {i18n.translate('AdminHTML.MarketingCampaign.Features.remainingDays', {
                            count: remainingDays,
                            nt_number: remainingDays
                          })}
                        </Badge>
                      </div>
                    )}
                    <div className="recommendation-type--select">
                      <RadioButton
                        checked={key === recommendationType}
                        disabled={!enabledRecommendationType}
                      />
                      <div>
                        <span className="btn-title">
                          <span className="btn-title-content">{name}</span>
                          {isAIPowered && (
                            <span className="decorative">
                              <Icon source={BadgeAI} />
                            </span>
                          )}
                          {mostUsed && (
                            <span className="btn-title-icon">
                              <Icon source={IconLikeContent} />
                            </span>
                          )}
                        </span>
                        <span className="btn-content">
                          <TextStyle variation="subdued">{description}</TextStyle>
                        </span>
                      </div>
                    </div>
                    {!enabledRecommendationType && !disableLockUnLock && (
                      <div className="btn-inline--recommendation-lock">
                        {isFreePlan && (
                          <div className="img-crown">
                            <img src={CrownPremium} alt="premium-crown-lock" />
                          </div>
                        )}
                        {i18n.translate(`AdminHTML.Common.Action.upgradeToUnlock`)}
                        <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
                      </div>
                    )}
                  </Button>
                </div>
              </Grid.Cell>
            );
          }
          return (
            <Grid.Cell columnSpan={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} key={key}>
              <div className={`button-large ${isSelected ? 'isSelected' : ''}`}>
                <Button fullWidth onClick={() => handleSelectRecommendationType(key, name)}>
                  <div className="recommendation-type--select">
                    <RadioButton checked={key === recommendationType} />
                    <div>
                      <span className="btn-title">
                        <span className="btn-title-content">{name}</span>
                        {isAIPowered && (
                          <span className="decorative">
                            <Badge status="attention">
                              <span className="badge-with-icon">
                                <Icon source={IconWandContent} />
                                {i18n.translate('AdminHTML.Recommendation.NewWidgetItem.aIPowered')}
                              </span>
                            </Badge>
                          </span>
                        )}
                        {mostUsed && (
                          <span className="btn-title-icon">
                            <Icon source={IconLikeContent} />
                          </span>
                        )}
                      </span>
                      <span className="btn-content">
                        <TextStyle variation="subdued">{description}</TextStyle>
                      </span>
                    </div>
                  </div>
                </Button>
              </div>
            </Grid.Cell>
          );
        })}
      </Grid>
    </div>
  );
};

class RecommendationType extends Component<ComposedProps, State> {
  state = {
    changePlanFromReviewIOModal: false
  };

  componentDidMount() {
    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  render() {
    const {
      i18n,
      addonStatus,
      statusStore,
      widget,
      tenantApp,
      handleCloseFullscreenPage,
      handleSelectPageType,
      handleSelectRecommendationType,
      notAllowAddon,
      currentStep,
      selectedPlan,
      dirtyOnStep
    } = this.props;
    const { pageType, recommendationType } = widget;

    // Get flag addon ai power recommendation here
    const addonRemainingCycles = _.get(addonStatus, 'trialInfo.remainingDays', 14);
    const isSubscribeNow =
      !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0;
    const disabledNextStep = _.isEmpty(pageType) || _.isEmpty(recommendationType);

    const shouldConfirmWhenLeave = !_.isEmpty(
      _.filter(dirtyOnStep, (value, key) => key !== `step_${currentStep}` && value)
    );

    return (
      <div className="BC-Page BC-Page__RecommendationNewWidgetItem BC-Page__RecommendationWidgetItem">
        <FullscreenBar
          onAction={() => handleCloseFullscreenPage(!shouldConfirmWhenLeave)}
          backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
          title={i18n.translate(`AdminHTML.Recommendation.NewWidgetItem.step1Title`)}
          actions={
            <ButtonGroup>
              <Button primary onClick={this.handleNext} disabled={disabledNextStep}>
                {i18n.translate('AdminHTML.Common.Action.next')}
              </Button>
            </ButtonGroup>
          }
          progress={25}
        />

        <div style={{ marginTop: '2rem' }}>
          <Page>
            <Layout>
              <Layout.Section>
                <Card>
                  {/* Select type */}
                  <Card.Section>
                    <DisplayText size="large">
                      {i18n.translate('AdminHTML.Recommendation.NewWidgetItem.stepSelectTypeTitle')}
                    </DisplayText>
                    {/* Select page type */}
                    <RenderSelectPageType
                      i18n={i18n}
                      pageType={pageType}
                      handleSelectPageType={handleSelectPageType}
                    />
                    {/* Select recommendation type */}
                    <RenderSelectRecommendationType
                      i18n={i18n}
                      tenantApp={tenantApp}
                      handleUnlock={() => this.handleUnlock(notAllowAddon)}
                      pageType={pageType}
                      selectedPlan={selectedPlan}
                      subscribedBefore={isSubscribeNow}
                      disableLockUnLock={notAllowAddon}
                      recommendationType={recommendationType}
                      handleSelectRecommendationType={handleSelectRecommendationType}
                    />
                  </Card.Section>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
          {/* Unlock addon to using feature */}
        </div>
        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={shouldConfirmWhenLeave}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={shouldConfirmWhenLeave}
          isAvailableFullscreen={true}
        />
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </div>
    );
  }

  handleUnlock = disable => {
    if (disable) return;

    if (this.props.useBoltOn) {
      this.setState({ changePlanFromReviewIOModal: true });
      return;
    }

    history.push(routes.subscription.package());
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { i18n, dirty } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle action next
  handleNext = () => {
    const { widget, updateState, currentStep, accessScopes } = this.props;
    const { recommendationType } = widget;
    const { hasSettings } = RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES[recommendationType];
    let step =
      hasSettings ||
      (recommendationType === 'recently-purchased' && !hasPermissionRecommendation(accessScopes))
        ? 1
        : 2;
    updateState('currentStep', currentStep + step);
  };

  /** Subscribe add on */
  // Reload page after subscribe
  handleCloseStatus = () => {
    this.props.getAddonStatus(AI_POWERED_RECOMMENDATION_NAME);
  };
}

const mapStateToProps = (state, props) => {
  return {
    tenantApp: state.oauth.tenantApp,
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    addonStatus: _.get(state.subscriptionV3, `addonStatus.${AI_POWERED_RECOMMENDATION_NAME}`, {}),
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    accessScopes: _.get(state.oauth, 'tenantApp.accessScopes', {}),
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false)
  };
};

export default withI18n()(connect(mapStateToProps, {})(RecommendationType));
